export function isBrowser() {
  return typeof window !== "undefined" && typeof window.crypto !== "undefined" && !!window.crypto.subtle;
}

export function getSubtle() {
  if (typeof crypto !== "undefined" && crypto.subtle) return crypto.subtle;
  // Node.js
  const { webcrypto } = require("node:crypto");
  return webcrypto.subtle;
}

// base64url encode/decode for both envs
export function base64urlEncode(bytes) {
  if (typeof Buffer !== "undefined" && Buffer.from) {
    // Node
    return Buffer.from(bytes).toString("base64")
      .replace(/=/g, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
  } else {
    // Browser
    // Convert bytes -> binary string -> base64 -> base64url
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin);
    return b64.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  }
}

export function base64urlDecode(str) {
  // restore padding
  const pad = str.length % 4 === 2 ? "=="
            : str.length % 4 === 3 ? "="
            : str.length % 4 === 0 ? ""
            : "=".repeat(4 - (str.length % 4));
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/") + pad;

  if (typeof Buffer !== "undefined" && Buffer.from) {
    const buf = Buffer.from(b64, "base64");
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  } else {
    // Browser
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
}

// Canonical JSON (stable ordering) to avoid hash drift due to key order
export function stableStringify(value) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    const items = value.map(v => stableStringify(v)).join(",");
    return `[${items}]`;
  }
  const keys = Object.keys(value).sort();
  const props = keys.map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",");
  return `{${props}}`;
}

// Optional replacer to omit ephemeral keys
export function defaultReplacer(key, value) {
  // drop keys starting with '.' or exactly 'ephemeral'
  if (key && key[0] === ".") return undefined;
  return value;
}

export function applyReplacerDeep(input, replacer = defaultReplacer, parentKey = "") {
  if (input === null || typeof input !== "object") return input;

  if (Array.isArray(input)) {
    return input.map(v => applyReplacerDeep(v, replacer));
  }

  const out = {};
  for (const k of Object.keys(input)) {
    const v = replacer(k, input[k]);
    if (typeof v !== "undefined") {
      out[k] = applyReplacerDeep(v, replacer, k);
    }
  }
  return out;
}

// Convert path IDs with null chars to arrays for JSON serialization
export function pathIdsToArrays(input) {
  if (input === null || typeof input !== "object") return input;

  if (Array.isArray(input)) {
    return input.map(v => {
      // Check if array element is a string with null character (path ID)
      if (typeof v === 'string' && v.includes('\0')) {
        const pathArray = v.split('\0').map(segment => {
          const [type, value] = segment.split(':');
          return type === 'n' ? parseInt(value) : value;
        });
        return `__pathArray:${JSON.stringify(pathArray)}`;
      }
      return pathIdsToArrays(v);
    });
  }

  const out = {};
  for (const k of Object.keys(input)) {
    const v = input[k];
    // Check if key contains null character (path ID)
    if (typeof k === 'string' && k.includes('\0')) {
      // Convert path ID to array format
      const pathArray = k.split('\0').map(segment => {
        const [type, value] = segment.split(':');
        return type === 'n' ? parseInt(value) : value;
      });
      out[`__pathArray:${JSON.stringify(pathArray)}`] = pathIdsToArrays(v);
    } else {
      out[k] = pathIdsToArrays(v);
    }
  }
  return out;
}

// Convert arrays back to path IDs with null chars after JSON parsing
export function arraysToPathIds(input) {
  if (input === null || typeof input !== "object") return input;

  if (Array.isArray(input)) {
    return input.map(v => {
      // Check if array element is a path array
      if (typeof v === 'string' && v.startsWith('__pathArray:')) {
        const pathArray = JSON.parse(v.substring(12)); // Remove '__pathArray:' prefix
        // Convert array back to path ID with null chars
        const pathId = pathArray.map(segment => {
          const type = typeof segment === 'number' ? 'n' : 's';
          return `${type}:${segment}`;
        }).join('\0');
        return pathId;
      }
      return arraysToPathIds(v);
    });
  }

  const out = {};
  for (const k of Object.keys(input)) {
    const v = input[k];
    // Check if key is a path array
    if (typeof k === 'string' && k.startsWith('__pathArray:')) {
      const pathArray = JSON.parse(k.substring(12)); // Remove '__pathArray:' prefix
      // Convert array back to path ID with null chars
      const pathId = pathArray.map(segment => {
        const type = typeof segment === 'number' ? 'n' : 's';
        return `${type}:${segment}`;
      }).join('\0');
      out[pathId] = arraysToPathIds(v);
    } else {
      out[k] = arraysToPathIds(v);
    }
  }
  return out;
}

export async function sha256(bytes) {
  const subtle = getSubtle();
  const digest = await subtle.digest("SHA-256", bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
  return new Uint8Array(digest);
}

export function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false;
  let acc = 0;
  for (let i = 0; i < a.length; i++) acc |= (a[i] ^ b[i]);
  return acc === 0;
}